(function () {
	const ZOOM_SCALE = 2.5;
	const DESKTOP_QUERY = '(pointer: fine) and (min-width: 990px)';

	const isDesktopZoomEnabled = () => window.matchMedia(DESKTOP_QUERY).matches;

	class ProductImageZoom extends HTMLElement {
		constructor() {
			super();
			this.toggleButton = null;
			this.lens = null;
			this.zoomSrc = '';
			this.isZoomed = false;
			this.swiper = null;
			this.boundHandleMove = this.handleMove.bind(this);
			this.boundHandleLeave = this.deactivateZoom.bind(this);
			this.boundHandleKeydown = this.handleKeydown.bind(this);
			this.boundHandleSlideChange = this.deactivateZoom.bind(this);
		}

		connectedCallback() {
			this.toggleButton = this.querySelector('.product__image-zoom__toggle');
			this.lens = this.querySelector('.product__image-zoom__lens');
			this.zoomSrc = this.dataset.zoomSrc || '';

			if (!this.toggleButton || !this.lens || !this.zoomSrc) return;

			this.lens.style.backgroundSize = `${ZOOM_SCALE * 100}%`;

			this.toggleButton.addEventListener('click', this.handleToggleClick.bind(this));
			this.toggleButton.addEventListener('keydown', this.handleToggleKeydown.bind(this));
			this.addEventListener('keydown', this.boundHandleKeydown);
			this.bindSlideChange();
		}

		disconnectedCallback() {
			this.deactivateZoom();
			this.removeEventListener('keydown', this.boundHandleKeydown);
			this.unbindSlideChange();
		}

		bindSlideChange() {
			const tryBind = () => {
				if (this.swiper) return true;

				const slide = this.closest('.swiper-slide');
				const swiperEl = slide?.closest('.swiper');

				if (!swiperEl?.swiper) return false;

				this.swiper = swiperEl.swiper;
				this.swiper.on('slideChange', this.boundHandleSlideChange);
				return true;
			};

			if (!tryBind()) {
				window.setTimeout(() => tryBind(), 300);
			}
		}

		unbindSlideChange() {
			if (this.swiper) {
				this.swiper.off('slideChange', this.boundHandleSlideChange);
				this.swiper = null;
			}
		}

		handleToggleClick(event) {
			if (!isDesktopZoomEnabled()) return;

			event.preventDefault();
			event.stopPropagation();

			if (this.isZoomed) {
				this.deactivateZoom();
				return;
			}

			this.activateZoom();
		}

		handleToggleKeydown(event) {
			if (!isDesktopZoomEnabled()) return;

			if (event.key === 'Enter' || event.key === ' ') {
				event.preventDefault();
				this.toggleButton.click();
			}
		}

		handleKeydown(event) {
			if (event.key === 'Escape' && this.isZoomed) {
				event.preventDefault();
				this.deactivateZoom();
				this.toggleButton.focus();
			}
		}

		activateZoom() {
			if (!isDesktopZoomEnabled() || this.isZoomed) return;

			this.isZoomed = true;
			this.classList.add('is-zoomed');
			this.toggleButton.setAttribute('aria-pressed', 'true');
			this.toggleButton.setAttribute(
				'aria-label',
				this.toggleButton.dataset.zoomActiveLabel || 'Image zoom active'
			);

			if (!this.lens.style.backgroundImage) {
				this.lens.style.backgroundImage = `url("${this.zoomSrc}")`;
			}

			this.addEventListener('mousemove', this.boundHandleMove);
			this.addEventListener('mouseleave', this.boundHandleLeave);
		}

		deactivateZoom() {
			if (!this.isZoomed || !this.toggleButton || !this.lens) return;

			this.isZoomed = false;
			this.classList.remove('is-zoomed');
			this.toggleButton.setAttribute('aria-pressed', 'false');
			this.toggleButton.setAttribute(
				'aria-label',
				this.toggleButton.dataset.zoomToggleLabel || 'Toggle image zoom'
			);

			this.removeEventListener('mousemove', this.boundHandleMove);
			this.removeEventListener('mouseleave', this.boundHandleLeave);
			this.lens.style.backgroundPosition = '50% 50%';
		}

		handleMove(event) {
			if (!this.isZoomed) return;

			const rect = this.getBoundingClientRect();
			const x = ((event.clientX - rect.left) / rect.width) * 100;
			const y = ((event.clientY - rect.top) / rect.height) * 100;

			this.lens.style.backgroundPosition = `${x}% ${y}%`;
		}
	}

	if (!customElements.get('product-image-zoom')) {
		customElements.define('product-image-zoom', ProductImageZoom);
	}
})();
